// Admin pricing (/admin/pricing) — harga tier one-time dari tabel pricing_tiers
// (migrasi 0010; menggantikan pricing.json). Hanya PRICE yang bisa diubah; Free
// terkunci Rp0 (keputusan produk). Simpan dua-langkah: ConfirmDialog menampilkan
// harga lama → baru terformat (rem typo — validasi rentang ada di server).
// Baris di sini DIPAKAI: checkout menghitung ulang amount dari tabel ini saat
// pesanan dibuat, dan build marketing membacanya lewat GET /pricing. Halaman ini
// tidak mengklaim penjualan buka/tutup — itu keadaan API (SALES_ENABLED), bukan
// fakta yang boleh ditulis permanen di markup.

import { useState, type FormEvent } from "react";
import { Glyph } from "@/ui";
import { dataClass } from "@ahcut/ui/recipes";
import { Alert, Badge, Button, Input, Label, fieldId } from "@/ui";
import { cn } from "@/lib/cn";
import { PageHeader } from "@/components/PageHeader";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Loading, ErrorState, EmptyState } from "@/components/States";
import { useAsync } from "@/lib/useAsync";
import { listAdminPricing, updateAdminPricingTier } from "@/lib/admin";
import { formatRupiah, formatDateTime } from "@/lib/format";
import type { AdminPricingTier } from "@/lib/types";
import { AdminPanel } from "@/components/admin/AdminPanel";
import { DataCell, DataRow, DataTable, NoValue, type DataColumn } from "@/components/admin/DataTable";
import { dataCellClass } from "@/components/admin/classes";

const COLUMNS: readonly DataColumn[] = [
  { label: "Tier" },
  { label: "Harga (Rp, utuh)" },
  { label: "Terakhir diubah" },
  { label: "Pratinjau", align: "end" },
];
const RANGE_HINT_ID = "admin-pricing-range";
const SAVE_ERROR_ID = "admin-pricing-error";

export function AdminPricing() {
  const { data, loading, error, reload } = useAsync<AdminPricingTier[]>(
    (s) => listAdminPricing(s),
    [],
  );

  // Draft per tier id — input harga yang sedang diketik (string, biar kosong sah).
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [confirming, setConfirming] = useState<AdminPricingTier | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const draftFor = (t: AdminPricingTier) => drafts[t.id] ?? String(t.price);
  const draftPrice = (t: AdminPricingTier) => Number(draftFor(t));

  function handleSubmit(e: FormEvent, t: AdminPricingTier) {
    e.preventDefault();
    setSaveError(null);
    const next = draftPrice(t);
    if (!Number.isInteger(next) || next <= 0) {
      setSaveError("Harga harus bilangan bulat rupiah yang positif.");
      return;
    }
    if (next === t.price) {
      setSaveError("Harga tidak berubah.");
      return;
    }
    setConfirming(t);
  }

  async function handleConfirm() {
    if (!confirming) return;
    setSaving(true);
    setSaveError(null);
    try {
      await updateAdminPricingTier(confirming.id, draftPrice(confirming));
      setConfirming(null);
      setDrafts((d) => {
        const { [confirming.id]: _dropped, ...rest } = d;
        return rest;
      });
      reload();
    } catch (err) {
      setConfirming(null);
      setSaveError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  }

  if (loading && !data) return <Loading label="Memuat harga…" />;
  if (error) return <ErrorState error={error} onRetry={reload} />;

  // The price inputs point at the range hint, and at the save error while one is shown.
  const describedBy = saveError ? `${RANGE_HINT_ID} ${SAVE_ERROR_ID}` : RANGE_HINT_ID;

  return (
    <div className="flex min-w-0 flex-col gap-6">
      <PageHeader
        title="Harga"
        lede="Harga tier di tabel pricing_tiers. Hanya harga yang bisa diubah; Free terkunci."
      />

      {!data || data.length === 0 ? (
        <EmptyState title="Katalog kosong">Seed migrasi 0010 belum diterapkan.</EmptyState>
      ) : (
        <div className="flex min-w-0 flex-col gap-4">
          <AdminPanel>
            <DataTable label="Harga per tier" columns={COLUMNS} stackBelow="40rem" busy={loading}>
              {data.map((t) => {
                const inputId = fieldId(`Harga ${t.name}`);
                return (
                  <DataRow key={t.id}>
                    <DataCell label="Tier">
                      <span className="flex flex-wrap items-center gap-2">
                        <span className="font-medium text-fg-0">{t.name}</span>
                        {t.highlight && <Badge>Unggulan</Badge>}
                      </span>
                    </DataCell>
                    <DataCell label="Harga (Rp, utuh)">
                      {t.id === "free" ? (
                        <span className="whitespace-nowrap">
                          <span className={cn(dataClass, "text-fg-0")}>{formatRupiah(t.price)}</span>{" "}
                          <span className="text-fg-2">(terkunci)</span>
                        </span>
                      ) : (
                        <form
                          className="flex flex-wrap items-center gap-2"
                          onSubmit={(e) => handleSubmit(e, t)}
                        >
                          {/* The column header names the control visually; the
                              label keeps the "Harga <tier>" name and id. */}
                          <Label htmlFor={inputId} className="sr-only">
                            {`Harga ${t.name}`}
                          </Label>
                          <Input
                            id={inputId}
                            size="sm"
                            type="number"
                            min={10000}
                            max={100000000}
                            step={1}
                            aria-describedby={describedBy}
                            className={cn(dataClass, "w-36")}
                            value={draftFor(t)}
                            onChange={(e) => setDrafts((d) => ({ ...d, [t.id]: e.target.value }))}
                          />
                          <Button type="submit" variant="secondary" size="sm" disabled={saving}>
                            Simpan
                          </Button>
                        </form>
                      )}
                    </DataCell>
                    <DataCell label="Terakhir diubah" className={dataCellClass}>
                      {formatDateTime(t.updated_at)}
                    </DataCell>
                    <DataCell label="Pratinjau" align="end" className={dataCellClass}>
                      {t.id === "free" ? (
                        <NoValue label="Tidak ada pratinjau" />
                      ) : (
                        formatRupiah(draftPrice(t) || 0)
                      )}
                    </DataCell>
                  </DataRow>
                );
              })}
            </DataTable>
          </AdminPanel>

          <p id={RANGE_HINT_ID} className="text-sm text-fg-2">
            Rentang harga tier berbayar: Rp 10.000 (floor kupon) s.d. Rp 100.000.000. Free selalu Rp 0.
          </p>

          {saveError && (
            <Alert id={SAVE_ERROR_ID} tone="danger" role="alert">
              {saveError}
            </Alert>
          )}
        </div>
      )}

      <ConfirmDialog
        open={confirming !== null}
        title={`Ubah harga ${confirming?.name ?? ""}?`}
        confirmLabel="Ya, ubah harga"
        busy={saving}
        onConfirm={handleConfirm}
        onCancel={() => setConfirming(null)}
      >
        {confirming && (
          <>
            <p className="flex flex-wrap items-center gap-2 text-fg-0">
              <span className={dataClass}>{formatRupiah(confirming.price)}</span>
              <Glyph name="arrowRight" className="size-4 text-fg-2" />
              <span className="sr-only">menjadi</span>
              <strong className={cn(dataClass, "font-semibold")}>
                {formatRupiah(draftPrice(confirming))}
              </strong>
            </p>
            <p>
              Harga baru berlaku untuk pesanan yang dibuat setelah ini; pesanan yang sudah ada tetap
              memakai harga yang dibekukan saat dibuat. Perubahan tercatat di audit.
            </p>
          </>
        )}
      </ConfirmDialog>
    </div>
  );
}
